import { useCallback, useEffect, useState } from 'react';
import useFetch from '../helpers/useFetch';

// Every check costs Shopify GraphQL calls against the shop's rate bucket, so the
// optional poll is slow and stops after a couple of minutes, and callers that
// only need a one-off answer leave it off. The API side throttles the outbound
// call too, so a burst of checks cannot become a burst of Shopify traffic.
const POLL_INTERVAL_MS = 10000;
const POLL_WINDOW_MS = 2 * 60 * 1000;

/**
 * The live state of a Shopify domain's Releva app embed.
 *
 * Returns a tri-state: `true` confirmed on, `false` confirmed off, `null`
 * nothing has answered yet. The domain's stored `appEmbedActive` is deliberately
 * not used as a seed — it is whatever was last written by a check somewhere
 * else, and a value inherited that way is not an observation, so reporting it as
 * one is how a merchant whose embed is fine ends up being told it is not.
 *
 * Shared rather than reimplemented per surface because the stored value only
 * changes when something calls this endpoint or the connector's nightly sweep
 * runs. A surface that tells a merchant their embed is off, offers them a link
 * to fix it, and then never looks again will keep saying it for up to a day
 * after they have fixed it.
 */
const useAppEmbedStatus = ({ domain, enabled = true, poll = false }) => {
  const fetch = useFetch();
  const { accountId, id: domainId } = domain ?? {};

  const [appEmbedActive, setAppEmbedActive] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(() => {
    if (!accountId || !domainId) {
      return Promise.resolve();
    }
    setChecking(true);
    return (
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/integrationStatus`, { method: 'GET' })
        .then(({ status, appEmbedActive: embedActive }) => {
          // 'unavailable' means the live read did not happen and the value that
          // came back is whatever was stored before, so it answers nothing.
          if (status !== 'unavailable') {
            setAppEmbedActive(embedActive ?? null);
          }
        })
        // A missing endpoint is indistinguishable from the connector being down.
        // Neither answers anything, so both leave the last answer standing.
        .catch(() => {})
        .finally(() => setChecking(false))
    );
  }, [fetch, accountId, domainId]);

  const confirmed = appEmbedActive === true;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    check();
  }, [enabled, check]);

  // The merchant leaves for the Shopify tab to flip the toggle and comes back;
  // that return is the moment worth re-checking on, and the one that lets a
  // warning clear itself instead of outliving the problem.
  useEffect(() => {
    if (!enabled || confirmed) {
      return undefined;
    }
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, confirmed, check]);

  useEffect(() => {
    if (!enabled || !poll || confirmed) {
      return undefined;
    }
    const deadline = Date.now() + POLL_WINDOW_MS;
    const interval = setInterval(() => {
      if (Date.now() > deadline) {
        clearInterval(interval);
        return;
      }
      check();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, poll, confirmed, check]);

  return { appEmbedActive, checking, check };
};

export default useAppEmbedStatus;
